import { useNavigate, useParams } from 'react-router-dom';
import { useCompanyRole } from '@/hooks/useCompanyRole';
import { useEditEmployeeForm } from '@/hooks/useEditEmployeeForm';
import { EmployeeDocumentsSection } from '@/components/company/EmployeeDocumentsSection';
import { CountryCombobox } from '@/components/insurance/CountryCombobox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated

export function EditEmployeePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const employeeId = id as Id<'companyEmployees'> | undefined; // AS_JUSTIFIED: one-time Id cast from route param at component top
  const { canEditEmployees } = useCompanyRole();
  const form = useEditEmployeeForm(employeeId);

  if (form.employee === undefined) {
    return (
      <div className="min-h-full bg-background-secondary p-8 space-y-4">
        <Skeleton className="h-9 w-40" />
        <Skeleton className="h-64 w-full max-w-2xl rounded-xl" />
      </div>
    );
  }

  if (!form.employee || !canEditEmployees) {
    return (
      <div className="min-h-full bg-background-secondary flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-foreground mb-1">
            {!form.employee ? 'Employee not found' : 'Access denied'}
          </h2>
          <Button variant="outline" onClick={() => navigate('/company/employees')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Employees
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary p-8">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/company/employees/${id}`)}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-xl font-semibold text-foreground">Edit — {form.employee.name}</h1>
      </div>

      <form onSubmit={form.handleSubmit} className="space-y-6 max-w-2xl">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Basic Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={form.name}
                onChange={e => form.setName(e.target.value)}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={e => form.setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={form.phoneNumber}
                  onChange={e => form.setPhoneNumber(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="position">Position</Label>
              <Input
                id="position"
                value={form.position}
                onChange={e => form.setPosition(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Personal Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input
                  id="postalCode"
                  value={form.postalCode}
                  onChange={e => form.setPostalCode(e.target.value)}
                  placeholder="12345"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={form.city}
                  onChange={e => form.setCity(e.target.value)}
                  placeholder="e.g. Amsterdam"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="identityDocumentNumber">ID / Passport Number</Label>
              <Input
                id="identityDocumentNumber"
                value={form.identityDocumentNumber}
                onChange={e => form.setIdentityDocumentNumber(e.target.value)}
                placeholder="AB123456"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Nationality</Label>
                <CountryCombobox
                  value={form.nationality}
                  onChange={form.setNationality}
                  placeholder="Select nationality"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dateOfBirth">Date of Birth</Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={form.dateOfBirth}
                  onChange={e => form.setDateOfBirth(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <EmployeeDocumentsSection
          identityDocFront={form.identityDocFront}
          identityDocBack={form.identityDocBack}
          drivingLicenseFront={form.drivingLicenseFront}
          drivingLicenseBack={form.drivingLicenseBack}
          editable
          onDocumentChange={form.handleDocumentChange}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/company/employees/${id}`)}
            disabled={form.isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={form.isSubmitting || !form.name.trim()}>
            {form.isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
}
